import { z } from 'zod'
import { HistorizedScopedModel, HistorizedScopedModelFieldNames, WithIdSchema, WithScopeIdSchema } from './base-models'
import { BidParams, MAPPING_DIMENSION_KEYS, MappingDimensionKey } from './bid-params-mapping'
import { BID_PARAM_TYPES, BidParamSpec, OBJECT_SUB_PARAM_TYPES, ObjectSubParamSpec, Platform, SeatBidParamSpec } from './bidderCatalog'

export const BIDDER_SEAT_TYPES = ['adm', 'node', 'spark_demand', 'spark_for_pubs'] as const
export const BidderSeatTypeSchema = z.enum(BIDDER_SEAT_TYPES)
export type BidderSeatType = z.infer<typeof BidderSeatTypeSchema>

export type BidderSeatProduct = 'adm' | 'pbs'

/** `type` carries the product: `adm` seats belong to Ad Management, every other type to Prebid Server. */
export const BIDDER_SEAT_TYPES_BY_PRODUCT: Record<BidderSeatProduct, readonly BidderSeatType[]> = {
  adm: ['adm'],
  pbs: ['node', 'spark_demand', 'spark_for_pubs'],
}

export const productOfBidderSeatType = (type: BidderSeatType): BidderSeatProduct => (type === 'adm' ? 'adm' : 'pbs')

/** Only Spark seat types can be internal; the same type with `isInternal: false` is a regular customer-facing seat. */
export const INTERNAL_CAPABLE_BIDDER_SEAT_TYPES: readonly BidderSeatType[] = ['spark_demand', 'spark_for_pubs']

export const canBeInternal = (type: BidderSeatType): boolean => INTERNAL_CAPABLE_BIDDER_SEAT_TYPES.includes(type)

/**
 * Spark For Pub is **two** seats on one bidder, and every branch that used to ask `type ===
 * 'spark_for_pubs'` has to say which of the two it means.
 *
 * The publisher half is the seat the customer owns: it carries the site×adUnit rows whose only bid param
 * is `isEnabled`, sits on the Server-side tab beside the other server-side integrations, and is the one
 * the customer named. The Spark half is Pubstack's: it carries the scope×placement rows holding the real
 * SSP params, and never leaves the Spark for Pubs tab. One document each, so neither has to store two
 * disjoint sets of mappings under one id and tell them apart by the shape of their dimensions.
 */
export const isSparkForPubPublisherSeat = (seat: Pick<BidderSeat, 'type' | 'isInternal'>): boolean => seat.type === 'spark_for_pubs' && !seat.isInternal

export const isSparkForPubSparkSeat = (seat: Pick<BidderSeat, 'type' | 'isInternal'>): boolean => seat.type === 'spark_for_pubs' && seat.isInternal

/**
 * Where a seat's mappings live: at scope level (one set of rows for the whole scope, keyed by placement)
 * or per media. Both Pubstack-operated seat kinds are scope-level, and `isInternal` alone answers it —
 * spelled out so the per-site aggregations and the `'scope'` sentinel read as one rule rather than two.
 */
export const isScopeLevelBidderSeat = (seat: Pick<BidderSeat, 'type' | 'isInternal'>): boolean => seat.type === 'spark_demand' || isSparkForPubSparkSeat(seat)

/** The internal twin is never shown to a customer, so its name only has to be unique and recognisable. */
export const buildSparkForPubSparkSeatName = (publisherSeatName: string): string => `${publisherSeatName}-int`

/**
 * What to call a Spark for Pubs seat on screen: the twin's suffix taken back off.
 *
 * The pair is one seat as far as anyone reading the admin screens is concerned — the publisher's half and
 * the Spark team's half of the same integration — and both halves are listed, so `appnexus` sitting a row
 * away from `appnexus-int` reads as two bidders rather than as one seat seen twice. The suffix stays on
 * disk: it is what keeps the two names unique, and every mapping and stack output already refers to it.
 *
 * The collision variants go too (`-int-0`, `-int-1`), since they exist for the same reason.
 */
export const displayBidderSeatName = (seat: Pick<BidderSeat, 'type' | 'isInternal' | 'name'>): string => (isSparkForPubSparkSeat(seat) ? seat.name.replace(/-int(-\d+)?$/, '') : seat.name)

/**
 * Spark for Pub seats share the `isEnabled` toggle UX with pbstck-bidder, but ride a real SSP adapter
 * (Adform, Criteo, …) whose catalog entry carries that SSP's full param set. The publisher owns none of
 * it — the placement IDs and their params are the Spark team's, held on the internal twin.
 */
export const SPARK_FOR_PUB_BID_PARAM_SPECS: BidParamSpec[] = [{ name: 'isEnabled', required: false, type: 'string' }]

/**
 * A key renamed in the seat settings, sent together with the seat it belongs to.
 *
 * An instruction, not state: the server applies it to the seat's mappings and never stores it. The server
 * cannot work it out on its own. Removing `customKey` and adding `newKey` in the same save looks exactly
 * like a rename, and only the screen knows what the user meant.
 */
export type ObjectBidParamKeyRename = {
  namespace: string
  from: string
  to: string
}

export type { ObjectSubParamSpec }

/** A key name holds no dot: the dot is the separator `expandObjectBidParams` puts between namespace and key. */
export const OBJECT_SUB_PARAM_NAME_PATTERN = /^[A-Za-z0-9_]+$/

/** Per namespace, so a seat cannot grow the mapping spreadsheet without limit. */
export const MAX_OBJECT_SUB_PARAMS = 20

/**
 * The mapping columns a seat really has: its catalog entry's params, with each `object` namespace replaced
 * by the sub-keys the seat declared under it.
 *
 * `ext` plus `{ name: 'customKey' }` gives `ext.customKey`. That is an ordinary dotted param: stored flat,
 * exported as its own column, and nested back into `{ ext: { customKey } }` by `formatBidParams` when the
 * stack is built. Nothing after this point knows a namespace was involved.
 *
 * Sub-keys take the namespace's `platform`, so a namespace limited to one integration limits everything
 * inside it. A namespace the seat has not configured gives no column at all.
 */
export const expandObjectBidParams = (catalogBidParams: BidParamSpec[], bidderSeat: Pick<BidderSeat, 'extraObjectBidParams'>): BidParamSpec[] =>
  catalogBidParams.flatMap((spec) =>
    spec.type !== 'object'
      ? [spec]
      : (bidderSeat.extraObjectBidParams?.[spec.name] ?? []).map((sub) => ({
          name: `${spec.name}.${sub.name}`,
          required: false,
          type: sub.type,
          ...(spec.platform ? { platform: spec.platform } : {}),
        }))
  )

/** The `object` params of a catalog entry: the namespaces a seat may declare sub-keys under. */
export const objectBidParamNames = (catalogBidParams: BidParamSpec[]): string[] => catalogBidParams.filter((spec) => spec.type === 'object').map((spec) => spec.name)

/**
 * Why a seat's `extraObjectBidParams` cannot be accepted, one message per problem. Empty when it is valid.
 *
 * The catalog decides which namespaces exist, so the zod schema cannot run this check: it never sees the
 * catalog entry.
 */
export const validateExtraObjectBidParams = (extraObjectBidParams: Record<string, ObjectSubParamSpec[]> | undefined, catalogBidParams: BidParamSpec[]): string[] => {
  const namespaces = new Set(objectBidParamNames(catalogBidParams))
  const catalogNames = new Set(catalogBidParams.map((spec) => spec.name))

  return Object.entries(extraObjectBidParams ?? {}).flatMap(([namespace, subParams]) => {
    if (!namespaces.has(namespace)) return [`'${namespace}' is not a bid param this bidder lets you add keys to.`]
    if (subParams.length > MAX_OBJECT_SUB_PARAMS) return [`'${namespace}' takes at most ${MAX_OBJECT_SUB_PARAMS} keys.`]

    const seen = new Set<string>()
    return subParams.flatMap((sub) => {
      if (seen.has(sub.name)) return [`'${namespace}.${sub.name}' is declared twice.`]
      seen.add(sub.name)
      // Without this, a catalog that later declares the same dotted param would give two columns one name.
      return catalogNames.has(`${namespace}.${sub.name}`) ? [`'${namespace}.${sub.name}' is already a bid param of this bidder.`] : []
    })
  })
}

/**
 * Publisher-facing view of a bidder seat.
 *
 * A Spark For Pub publisher seat stores its own dimensions like any other seat, so the view no longer
 * has to rewrite them — what it still restricts is the **bid params**: the publisher edits one,
 * `isEnabled`, and the catalog's specs never reach the spreadsheet or the CSVs. Any other seat, the
 * internal twin included, gets its specs from the catalog as usual.
 *
 * Lives here rather than beside the mapping types it serves because it asks a question about a *seat*,
 * and `bid-params-mapping` cannot import a value from this module without closing an import cycle.
 */
export const getPublisherBidderSeatView = (bidderSeat: BidderSeat): { bidderSeat: BidderSeat; bidParamSpecs?: BidParamSpec[] } =>
  isSparkForPubPublisherSeat(bidderSeat) ? { bidderSeat, bidParamSpecs: SPARK_FOR_PUB_BID_PARAM_SPECS } : { bidderSeat, bidParamSpecs: undefined }

/**
 * A server-side seat that bids on the whole inventory rather than on Node's medias alone.
 *
 * Server-side integration comes in two shapes, and only `platform` tells them apart in the seat list:
 * the deprecated `adm` one, which predates Node and stays inside One, and the `node` one, which is the
 * current way. The current one is Pubstack's Prebid Server, and the Prebid Server a scope runs does not
 * stop at the medias Node itself deploys — a One media does server-side bidding through it too. So a
 * `node` seat is mapped across every media, each offering the ad units its own product holds.
 *
 * Asked of `type` rather than of `platform`: `platform: 'server'` is also true of the deprecated seat,
 * which is exactly the one this must not widen.
 *
 * The **publisher** half of a Spark For Pub pair answers the same way, and for the same reason: it bids
 * through that same Prebid Server, so its inventory is the scope's rather than Node's alone — a One
 * media the publisher serves is inventory the seat can be mapped on. The rows it carries are already
 * site×adUnit shaped, so nothing about the mapping changes; what widens is which medias and which ad
 * units the spreadsheet and the CSVs offer.
 *
 * The internal twin is deliberately excluded. It is keyed by `placementId`, not by site×adUnit, so a
 * wider media list would give it nothing to address — which is why this asks `isInternal` and not just
 * the type.
 */
export const isCrossInventoryServerSeat = (seat: Pick<BidderSeat, 'type' | 'isInternal'>): boolean => seat.type === 'node' || isSparkForPubPublisherSeat(seat)

// A bid adjustment is a percentage / 100, and the percentage input allows a step of 0.01% — so the
// stored fraction is meaningful to 4 decimals (87.45% → 0.8745). Computing `percentage / 100` in
// JS yields IEEE-754 artifacts (87.4 / 100 → 0.8740000000000001); round them away so the value
// stored in adm_bidder_seats stays clean and never flags a false out-of-sync.
export const BID_ADJUSTMENT_DECIMALS = 4
export const roundBidAdjustment = (value: number): number => Number(value.toFixed(BID_ADJUSTMENT_DECIMALS))

export const SPARK_FOR_PUB_MIN_DECLARED_PLACEMENTS = 5
export const SPARK_FOR_PUB_MAX_DECLARED_PLACEMENTS = 10

export type DeclaredPlacement = {
  placementId: string
  bidParams: BidParams
}

/** What each half of a Spark For Pub pair is mapped by — the publisher toggles ad units, the Spark team fills placements. */
export const SPARK_FOR_PUB_PUBLISHER_MAPPING_DIMENSIONS: MappingDimensionKey[] = ['siteId', 'adUnitId']
export const SPARK_FOR_PUB_SPARK_MAPPING_DIMENSIONS: MappingDimensionKey[] = ['placementId']

// Common object for ADM and PBS
export interface BidderSeat extends HistorizedScopedModel {
  name: string
  /**
   * The **id** of the seat's bidder catalog entry, not its `code`. The two differ whenever a demand
   * partner rides another adapter (`{id: 'gingerad', code: 'msft'}`); resolve with
   * `getBidderCatalogById`, and read `catalog.code` when you need the Prebid adapter name.
   */
  bidderCode: string
  platform: Platform
  bidAdjustment: {
    enabled: boolean
    value?: number
  }
  mappingDimensions: MappingDimensionKey[]
  type: BidderSeatType
  /** Internal seats are operated by Pubstack: admins own their mappings and they are hidden from client users. */
  isInternal: boolean
  mappingCountPerSite?: Record<string, number>
  /** Only meaningful for `type === 'spark_for_pubs'`: SSP placement IDs declared at seat creation, informational for the Spark team. */
  declaredPlacements?: DeclaredPlacement[]
  /**
   * Set on the **internal** half of a Spark For Pub pair, pointing at the publisher seat it serves.
   *
   * The two halves are one bidder as far as the runtime is concerned: the publisher's `isEnabled` row
   * reaches Prebid Server in the `pbs` stack, and the real SSP params reach it in the `spark-for-pub`
   * one, joined by nothing but the **bidder name** both stacks emit. Splitting the pair into two
   * documents therefore cannot split the name, so stack generation reads it back through this link and
   * emits the publisher seat's name on both sides. Admin-owned: re-stamped from the stored document on
   * every update, never trusted from the payload.
   */
  publisherBidderSeatId?: string
  /**
   * Seat-level bid params, copied from the catalog entry's `seatBidParams` at creation. Merged into
   * every mapping's params at stack generation and never persisted on a mapping, so they stay out of
   * the spreadsheet and the CSVs. Enforced (`editable: false`) entries are re-synced from the
   * catalog on every write.
   */
  seatBidParams?: SeatBidParamSpec[]
  /**
   * The sub-keys this seat declares under each `object` param of its catalog entry, keyed by that param's
   * name: `{ ext: [{ name: 'customKey', type: 'string' }] }`.
   *
   * The catalog decides which namespaces can be extended, the seat decides what goes in them. Each entry
   * becomes one mapping column named `ext.customKey`, so its values are stored, exported and nested back
   * like any other dotted param. See `expandObjectBidParams`.
   */
  extraObjectBidParams?: Record<string, ObjectSubParamSpec[]>
}

/**
 * What the seat PUT accepts: the seat itself, plus instructions the server applies and does not store.
 * Keeping them off `BidderSeat` is what stops them being persisted by the whole-document replace.
 */
export type BidderSeatUpdatePayload = BidderSeat & { keyRenames?: ObjectBidParamKeyRename[] }

/** `type` and `isInternal` are optional on create: the server defaults them from the mount's product. */
export type CreateBidderSeat = Omit<BidderSeat, HistorizedScopedModelFieldNames | 'type' | 'isInternal'> & {
  type?: BidderSeatType
  isInternal?: boolean
}

export const BidderSeatSchema = WithIdSchema.extend(WithScopeIdSchema.shape).extend({
  name: z.string(),
  bidderCode: z.string(),
  bidAdjustment: z
    .object({
      enabled: z.boolean(),
      value: z
        .number()
        .optional()
        .transform((v) => (v === undefined ? v : roundBidAdjustment(v))),
    })
    .refine((data) => !data.enabled || data.value !== undefined, {
      message: 'value must be defined when enabled is true',
      path: ['value'],
    }),
  mappingDimensions: z.array(z.enum(MAPPING_DIMENSION_KEYS)).transform((dims) => (dims ? Array.from(new Set(dims)) : dims)),
  // `type` and `isInternal` stay optional on the wire: an old browser tab PUTs without them, and the
  // server re-stamps both from the stored document rather than trusting the payload.
  type: BidderSeatTypeSchema.optional(),
  isInternal: z.boolean().optional(),
  mappingCountPerSite: z.record(z.string(), z.number()).optional(),
  platform: z.enum(['client', 'server']).default('server'),
  declaredPlacements: z.array(z.object({ placementId: z.string(), bidParams: z.record(z.string(), z.any()) })).optional(),
  publisherBidderSeatId: z.string().optional(),
  // The PUT handler parses through this schema and the service replaces the whole document, so a
  // field missing here is silently wiped on every seat update.
  seatBidParams: z
    .array(
      z.object({
        name: z.string(),
        type: z.enum(BID_PARAM_TYPES),
        editable: z.boolean(),
        default: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]).optional(),
      })
    )
    .optional(),
  // An instruction the update path reads and strips, see `ObjectBidParamKeyRename`. It is in this schema
  // only because the PUT body is parsed through it. Persisting it would be a bug.
  keyRenames: z
    .array(
      z.object({
        namespace: z.string(),
        from: z.string(),
        to: z.string(),
      })
    )
    .optional(),
  // Same warning as `seatBidParams` above: a field missing from this schema is wiped on every seat update.
  // The keys are namespace names. `validateExtraObjectBidParams` checks them against the catalog entry,
  // which the schema cannot do: it never sees the catalog.
  extraObjectBidParams: z
    .record(
      z.string(),
      z.array(
        z.object({
          name: z.string().regex(OBJECT_SUB_PARAM_NAME_PATTERN, 'A key holds letters, digits and underscores, and no dot.'),
          type: z.enum(OBJECT_SUB_PARAM_TYPES),
        })
      )
    )
    .optional(),
})

// Sentinel key used in `mappingCountPerSite` for scope-level (internal) bidder seats,
// whose mappings have no `dimensions.siteId`. Treat the presence of this key as "mapped".
export const SCOPE_LEVEL_MAPPING_KEY = 'scope'

export const PBSTCK_BIDDER_SEAT_NAME = 'Spark'
export const PBSTCK_BIDDER_SEAT_CODE = 'pbstck-bidder'

/** Where a Spark demand seat stands on a given scope. `no-seat` means the scope is under Spark contract but holds no seat for this bidder. */
export type SparkSeatScopeStatus = 'mapped' | 'no-mapping' | 'no-seat'

export type SparkSeatScopeDetail = {
  scopeId: string
  status: SparkSeatScopeStatus
}

/** Cross-scope deployment counters for a Spark demand seat, keyed by bidder code. */
export type SparkSeatScopeCounts = {
  bidderCode: string
  /** Scopes holding a seat for this bidder, mapped or not. */
  scopeCount: number
  /** Scopes whose seat carries a scope-level bid params mapping. */
  mappedScopeCount: number
}

export type SparkSeatScopeCountsSummary = {
  /** Scopes onboarded on Spark — the reference the per-bidder counts are read against. A bidder can exceed it, since disabling it keeps the seats it already holds. */
  sparkScopeCount: number
  counts: SparkSeatScopeCounts[]
}

export const PBSTCK_BIDDER_SEAT_NAME_REGEX = /^[a-zA-Z0-9\-_]+\-spark$/
export const PBSTCK_BIDDER_SEAT_NAME_ERROR = 'No space or special character except - and _ and must end with -spark'
export const BIDDER_SEAT_NAME_REGEX = /^[a-zA-Z0-9\-_]+(?<!-(spark|pbstck))(?<!_[dD][rR][dD])$/
export const BIDDER_SEAT_NAME_ERROR = 'No space or special character except - and _ and must not end with -spark, -pbstck or _drd'
export const MARKETPLACE_BIDDER_SEAT_NAME_REGEX = /^[a-zA-Z0-9\-_]+\-pbstck$/
export const MARKETPLACE_BIDDER_SEAT_NAME_ERROR = 'No space or special character except - and _ and must end with -pbstck'

/** Suffix of the pre-filled (but freely editable) name for a Spark For Pub seat. Not enforced: Spark For Pub names follow `BIDDER_SEAT_NAME_REGEX` like node seats. */
export const SPARK_FOR_PUB_BIDDER_SEAT_NAME_SUFFIX = '-s4p'

/** Suffix of the pre-filled (but freely editable) name for a server-side seat. Not enforced, same as the Spark For Pub one. */
export const SERVER_SIDE_BIDDER_SEAT_NAME_SUFFIX = '-s2s'

/** Spark Demand is Pubstack's own demand, sold by Pubstack — never created by a customer. */
export const SPARK_DEMAND_BIDDER_SEAT_NAME_SUFFIX = '-spark'

/**
 * The bid adjustment every Spark Demand seat is created with. Shared so a seat created from the internal
 * tab is indistinguishable from one created by `onboardScope` — the two used to hardcode the same literal.
 */
export const SPARK_DEMAND_DEFAULT_BID_ADJUSTMENT = 0.8

export const buildSparkForPubBidderSeatName = (bidderCode: string): string => `${bidderCode}${SPARK_FOR_PUB_BIDDER_SEAT_NAME_SUFFIX}`

export const toSparkBidderCode = (bidderCode: string): string => `${bidderCode}-spark`
/** Declared placement IDs are generated, not user-edited: `<bidderCode>-placement-<n>` with `n` 1-based. */
export const buildSparkForPubPlacementId = (bidderCode: string, index: number): string => `${bidderCode}-placement-${index}`

export const normalizeBidderSeatName = (bidderCatalogName: string): string => bidderCatalogName.replace(/[\s.()]/g, '')

// The gingerad-specific 0.8 default moved to the catalog entry's `defaultBidAdjustment`, so any
// partner bidding net-publisher declares it as data rather than needing a branch at seat creation.
